import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { useAppDispatch, useAppSelector } from "redux/hooks"
import { selectContent, closeDialog } from "redux/features/dialog/dialogSlice"
import {
  selectSubscriptionById,
  selectFetchStatus,
} from "redux/features/subscription/subscriptionSlice"
import { fetchSubscription } from "redux/features/subscription/subscriptionThunk"
import { Progress } from "components/ui/Spinner"
import Close from "components/ui/ModalClose"
import LoadableEntity from "components/ui/LoadableEntity"
import {
  LoaderContainer,
  ErrorContainer,
  Headline,
  Title,
  Description,
} from "hocs/features/modal/elements"
import { SubscriptionType } from "types"

export function withModalSubscription<GenericType>(
  WrappedComponent: React.ComponentType<GenericType>,
) {
  const WithModalSubscription = (props: GenericType) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { t } = useTranslation()

    const content = useAppSelector((state) => selectContent(state))
    const { subscriptionId } = content

    const subscription = subscriptionId
      ? useAppSelector(
          (state) =>
            selectSubscriptionById(state, subscriptionId) as SubscriptionType,
        )
      : null

    const fetchStatus = useAppSelector((state) => selectFetchStatus(state))

    useEffect(() => {
      const handleFetchSubscription = () => {
        if (!fetchStatus.isFetching && subscriptionId) {
          dispatch(fetchSubscription(subscriptionId))
        }
      }

      handleFetchSubscription()
    }, [subscriptionId])

    const handleClose = () => {
      dispatch(closeDialog())
      navigate("/")
    }

    const renderLoader = () => {
      return (
        <LoaderContainer>
          <Progress color="primary" />
        </LoaderContainer>
      )
    }

    const renderError = () => {
      return (
        <ErrorContainer>
          <Close onClick={handleClose} />
          <Headline>
            <Title variant="h3">
              {t("features.subscription.loading.title")}
            </Title>
            <Title variant="h5">
              {t("features.subscription.loading.error.title")}
            </Title>
            <Description variant="h6">
              {t("features.subscription.loading.error.description")}
            </Description>
          </Headline>
        </ErrorContainer>
      )
    }

    return (
      <LoadableEntity
        entity={subscription}
        fetchStatus={fetchStatus}
        renderLoading={renderLoader}
        renderError={renderError}
      >
        {(subscription) => (
          <WrappedComponent
            {...props}
            subscription={subscription}
            fetchStatus={fetchStatus}
          />
        )}
      </LoadableEntity>
    )
  }

  return WithModalSubscription
}
